import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, CheckCircle, ArrowRight, Star, Shield, Clock, Home } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track page view and clicks
const trackEvent = async (eventType, eventData = {}) => {
  try {
    await fetch(`${API_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        event_data: eventData,
        timestamp: new Date().toISOString(),
        referrer: document.referrer || 'direct',
        user_agent: navigator.userAgent
      })
    });
  } catch (err) {
    console.log('Analytics tracking error:', err);
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [hasTrackedView, setHasTrackedView] = useState(false);

  useEffect(() => {
    // Track page view once on load
    if (!hasTrackedView) {
      trackEvent('page_view', { page: 'landing_100cltv' });
      setHasTrackedView(true);
    }
  }, [hasTrackedView]);

  const handleCTAClick = (ctaType) => {
    trackEvent('cta_click', { cta: ctaType, page: 'landing_100cltv' });
    // Open phone dialer
    window.location.href = 'tel:305-606-2423';
  };

  const handleApplyClick = () => {
    trackEvent('cta_click', { cta: 'apply_now', page: 'landing_100cltv' });
    navigate('/apply');
  };

  const programs = [
    {
      title: '100%',
      subtitle: 'CLTV Program',
      features: [
        '100% CLTV FHA Loan (Combined 96.5% + 3.5% DPA)',
        'No Moderate Income Restrictions',
        'Minimum FICO 600',
        'Manual Underwrite Allowed',
        'FHA Purchase Only'
      ],
      highlight: 'Most Popular'
    },
    {
      title: '100% PLUS',
      subtitle: 'Enhanced Program',
      features: [
        '95% Max LTV with 3.5% DPA (100% CLTV)',
        'No Maximum Income Restrictions',
        'No First Time Home Buyer Requirement',
        'Minimum FICO 600',
        'Manual Underwrites Available',
        'Conforming and High Balance Loan Limits'
      ],
      highlight: 'Most Flexible'
    },
    {
      title: '101.5% DELUXE',
      subtitle: 'Premium Program',
      features: [
        '105% CLTV (96.5% First + 0.5% DPA)',
        'No FTB Requirement',
        'Single Family Primary Residence',
        'FHA 30-YR Fixed Purchase'
      ],
      highlight: 'Maximum Coverage'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background pattern - pointer-events-none to allow clicks through */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full px-4 py-2 mb-6">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 font-semibold text-sm">PURCHASE 100% FINANCING</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                <span className="text-yellow-400">100% CLTV</span>
                <br />
                <span className="text-white">FINANCING</span>
              </h1>
              
              <p className="text-xl text-blue-100 mb-8 max-w-lg">
                Get into your dream home with <strong className="text-yellow-400">ZERO down payment</strong>. 
                Our exclusive programs make homeownership possible for everyone.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                <Button 
                  onClick={() => handleCTAClick('hero_call')}
                  size="lg"
                  className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-lg px-8 py-6 rounded-full shadow-lg shadow-yellow-500/30 transition-all hover:scale-105"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  CALL NOW: 305-606-2423
                </Button>
                <Button 
                  onClick={handleApplyClick}
                  size="lg"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-bold text-lg px-8 py-6 rounded-full transition-all"
                  data-testid="hero-apply-btn"
                >
                  APPLY ONLINE
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>

              <div className="flex items-center gap-6 justify-center lg:justify-start text-white/80">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  <span className="text-sm">Licensed & Insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-400" />
                  <span className="text-sm">Fast Pre-Approval</span>
                </div>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_leadtrack-pro-2/artifacts/jvf8ujzx_Jose_Ares_moneyman.jpg"
                  alt="Jose Ares - Mortgage Specialist"
                  className="w-full max-w-md mx-auto rounded-2xl shadow-2xl shadow-black/50"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-72 h-72 bg-yellow-500/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-4 -left-4 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-16 lg:py-24 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              CHECK OUT OUR <span className="text-yellow-600">LTV PROGRAMS</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              We have a program for every situation. Compare our options below and find the perfect fit for your homeownership journey.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <div 
                key={index}
                className="relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
              >
                {/* Highlight Badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                    {program.highlight}
                  </span>
                </div>

                <div className="text-center mb-6 pt-4">
                  <h3 className="text-3xl font-black text-yellow-400">{program.title}</h3>
                  <p className="text-blue-200 font-medium">{program.subtitle}</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {program.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={() => handleCTAClick(`program_${index + 1}`)}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 rounded-xl transition-all"
                  data-testid={`program-${index + 1}-cta`}
                >
                  GET APPROVED NOW
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About / Trust Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_leadtrack-pro-2/artifacts/axpnuzvi_IMG_7954.PNG"
                alt="Jose Ares - Your Trusted Mortgage Expert"
                className="w-full max-w-sm mx-auto rounded-2xl shadow-xl"
              />
            </div>

            {/* Content */}
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Meet <span className="text-blue-600">Jose Ares</span>
                <br />Your Trusted Mortgage Expert
              </h2>
              
              <p className="text-lg text-slate-600 mb-6">
                With years of experience in the mortgage industry, I specialize in helping families achieve 
                their dream of homeownership through our exclusive 100% financing programs.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-slate-700 font-medium">Hundreds of families helped into new homes</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Home className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-slate-700 font-medium">Specialized in first-time homebuyer programs</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <Star className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span className="text-slate-700 font-medium">5-star rated mortgage specialist</span>
                </div>
              </div>

              <Button 
                onClick={() => handleCTAClick('about_call')}
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-lg px-8 py-6 rounded-full shadow-lg transition-all hover:scale-105"
                data-testid="about-call-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                Schedule a Free Consultation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-yellow-500 to-yellow-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 mb-6">
            Ready to Own Your Dream Home?
          </h2>
          <p className="text-xl text-slate-800 mb-8 max-w-2xl mx-auto">
            Don't let down payment stop you. Our 100% financing programs make homeownership possible TODAY.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => handleCTAClick('final_call')}
              size="lg"
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold text-xl px-10 py-7 rounded-full shadow-xl transition-all hover:scale-105"
              data-testid="final-call-btn"
            >
              <Phone className="w-6 h-6 mr-2" />
              CALL 305-606-2423
            </Button>
            <Button 
              onClick={handleApplyClick}
              size="lg"
              variant="outline"
              className="border-3 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-xl px-10 py-7 rounded-full transition-all"
              data-testid="final-apply-btn"
            >
              APPLY NOW
              <ArrowRight className="w-6 h-6 ml-2" />
            </Button>
          </div>

          <p className="mt-8 text-slate-700 text-sm">
            NMLS# Licensed | Equal Housing Opportunity | © {new Date().getFullYear()} Axe Capital Lending
          </p>
        </div>
      </section>
    </div>
  );
}
